/**
 * Convert hex colour value to RGB array.
 *
 * @param hex hex colour string.
 * @returns RGB colour Triples.
 */
export function hexToRgb(hex: string): [number, number, number] {
  hex = hex.replace(/^#/, "");
  if (hex.length === 3) {
    hex = hex.replace(/(.)/g, "$1$1");
  }
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return [r, g, b];
}

/**
 * Convert RGB array to hex colour value.
 *
 * @param rgb RGB colour Triples.
 * @returns hex colour string.
 */
export function rgbToHex(rgb: [number, number, number]): string {
  return (
    "#" +
    rgb
      .map((component) => ("0" + Math.round(component).toString(16)).slice(-2))
      .join("")
  );
}

/**
 * Darken colour by percentage
 *
 * @param hex hex colour string.
 * @param percentage percentage to darken.
 * @returns new hex colour string.
 */
export function darken(hex: string, percentage: number): string {
  const rgb = hexToRgb(hex);
  const darkenedRgb = rgb.map((component) =>
    Math.max(component * (1 - percentage / 100), 0)
  ) as [number, number, number];
  return rgbToHex(darkenedRgb);
}

/**
 * Fade colour by percentage.
 *
 * @param hex hex colour string.
 * @param percentage 0 to 100 alpha percentage.
 * @returns rgba colour string.
 */
export function fade(hex: string, percentage: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${percentage / 100})`;
}

/**
 * Tint colour by amount.
 *
 * @param hex hex colour string.
 * @param amount 0 to 100.
 * @returns new hex colour string.
 */
export function tint(hex: string, amount: number): string {
  const rgb = hexToRgb(hex);
  const tintedRgb = rgb.map((component) =>
    Math.min(255, component + ((255 - component) * amount) / 100)
  ) as [number, number, number];
  return rgbToHex(tintedRgb);
}

/**
 * Lighten colour by amount.
 *
 * @param hex hex colour string.
 * @param amount 0 to 100.
 * @returns new hex colour string.
 */
export function lighten(hex: string, amount: number): string {
  const rgb = hexToRgb(hex);
  const lightenedRgb = rgb.map((component) =>
    component + ((255 - component) * amount) / 100
  ) as [number, number, number];
  return rgbToHex(lightenedRgb);
}

/**
 * Get luma from rgb colour.
 *
 * @param red Red component of rgb colour.
 * @param green Green component of rgb colour.
 * @param blue Blue component of rgb colour.
 * @returns The luma value in range [0, 1) of rgb colour.
 */
export function luma(red: number, green: number, blue: number): number {
  return (red * 0.2126 + green * 0.7152 + blue * 0.0722) / 255;
}
